/**
 * #428: pure helpers behind the grid import wizard (no React, unit-tested).
 */
import type { GridAliases, GridBasisConfig, GridProblem, GridSampleRow, GridSheetConfig } from '@/services/commissionGrids';

export const ROLE_LABELS: Record<string, string> = {
  rates_matrix: 'Rates: one rate per row x column (cities / states across)',
  rates_long: 'Rates: one rate per row (state-wise list)',
  conditions: 'Conditions (shown on the grid)',
  incremental: 'Incremental slab rule',
  class_mapper: 'Class mapper (segment -> class)',
  decline_rto: 'Declined / excluded RTOs',
  ignore: 'Ignore this sheet',
};

export const FIELD_LABELS: Record<string, string> = {
  segment: 'Segment / product',
  tonnage: 'Tonnage (GVW)',
  age: 'Vehicle age',
  fuel: 'Fuel',
  policy_type: 'Policy type / section',
  business_type: 'Business type (new / renewal / rollover)',
  cc: 'CC band',
  ncb: 'NCB',
  addon: 'Add-on',
  nil_dep: 'Nil Dep',
  seating: 'Seating',
  class_group: 'Insurer class',
  geo: 'Geography (city / region / state)',
  geo_state: 'State group (context)',
  geo_zone: 'Zone (context)',
  ignore: 'Not a key (ignore)',
};

export const BASIS_LABELS: Record<string, string> = {
  od_premium: 'OD premium',
  tp_premium: 'TP premium',
  net_premium: 'Net premium',
  gross_premium: 'Gross premium',
  total_premium: 'Total premium',
};

const BASIS_SOURCE_LABELS: Record<string, string> = {
  row: 'stated in the cell',
  sheet_text: 'stated on the sheet',
  mapping_declared: 'declared in the mapping',
};

export const RATE_ROLES = ['rates_matrix', 'rates_long'];

/** "5, 6 7" -> [5, 6, 7]; anything that is not a positive whole number is dropped. */
export function parseRowList(text: string): number[] {
  return text
    .split(/[\s,]+/)
    .map((t) => Number(t))
    .filter((n) => Number.isInteger(n) && n > 0);
}

/** "BQ, br" -> ["BQ", "BR"]; anything that is not a column letter is dropped. */
export function parseColumnList(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((t) => t.trim().toUpperCase())
    .filter((t) => /^[A-Z]{1,3}$/.test(t));
}

/** #590: add a key column the suggested mapping missed. Returns the patch, or null when the column letter is invalid. */
export function addKeyColumn(sc: GridSheetConfig, column: string, field: string): Partial<GridSheetConfig> | null {
  const [col] = parseColumnList(column);
  if (!col || !field) return null;
  return { key_columns: { ...(sc.key_columns ?? {}), [col]: field } };
}

/** #590: add a header row the suggested mapping missed (it also joins header_rows, which the server requires). */
export function addHeaderRow(sc: GridSheetConfig, row: string, field: string): Partial<GridSheetConfig> | null {
  const [r] = parseRowList(row);
  if (!r || !field) return null;
  return {
    header_rows: Array.from(new Set([...(sc.header_rows ?? []), r])).sort((a, b) => a - b),
    column_fields: { ...(sc.column_fields ?? {}), [String(r)]: field },
  };
}

/** Rate sheets whose premium basis is neither stated on the sheet nor declared yet. */
export function sheetsMissingBasis(sheets: Record<string, GridSheetConfig>): string[] {
  return Object.entries(sheets)
    .filter(([, sc]) => RATE_ROLES.includes(sc.role))
    .filter(([, sc]) => {
      const basis = sc.basis;
      if (!basis) return true;
      if (basis.source === 'declared') return !basis.value;
      if (basis.source === 'sheet_text') return !basis.cell;
      return false;
    })
    .map(([key, sc]) => sc.title ?? key);
}

/** One block of an incremental rule sheet (#430). */
export interface SlabBlockConfig {
  class?: string;
  class_column?: string;
  slab_column: string;
  increment_column: string;
  data_start?: number;
  data_end?: number | null;
  volume_basis?: GridBasisConfig;
}

/** The blocks of an incremental sheet, or [] for any other sheet. */
export function slabBlocks(sc: GridSheetConfig): SlabBlockConfig[] {
  return sc.role === 'incremental' && Array.isArray(sc.blocks) ? (sc.blocks as SlabBlockConfig[]) : [];
}

/**
 * #430: incremental blocks whose slab volume basis is neither stated on the
 * sheet nor declared ("Slabs" with no basis, SPEC-425 Q5). The server rejects
 * every slab row of such a block with E_BASIS_MISSING.
 */
export function slabBlocksMissingBasis(sheets: Record<string, GridSheetConfig>): string[] {
  const out: string[] = [];
  for (const [key, sc] of Object.entries(sheets)) {
    slabBlocks(sc).forEach((block, i) => {
      const vb = block.volume_basis;
      const ok = vb && ((vb.source === 'sheet_text' && vb.cell) || (vb.source === 'declared' && vb.value));
      if (!ok) out.push(`${sc.title ?? key} (slab block ${block.class || block.class_column || i + 1})`);
    });
  }
  return out;
}

export interface SlabTable {
  classGroup: string;
  volumeBasis: string;
  slabs: { range: string; points: string; sourceRef: string }[];
}

function amountText(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

/** The preview's slab rows grouped per class, lowest slab first: "20,001 - 50,000: +1.50 pts". */
export function slabTables(
  slabs: { class_group: string; from_amount: string; to_amount: string | null; increment_points: string; volume_basis: string; source_ref: string }[],
): SlabTable[] {
  const byClass = new Map<string, SlabTable>();
  const ordered = [...slabs].sort((a, b) => Number(a.from_amount) - Number(b.from_amount));
  for (const s of ordered) {
    const table = byClass.get(s.class_group) ?? { classGroup: s.class_group, volumeBasis: s.volume_basis, slabs: [] };
    table.slabs.push({
      range: s.to_amount == null ? `${amountText(s.from_amount)} and above` : `${amountText(s.from_amount)} - ${amountText(s.to_amount)}`,
      points: s.increment_points,
      sourceRef: s.source_ref,
    });
    byClass.set(s.class_group, table);
  }
  return [...byClass.values()];
}

export interface LabelProblem {
  field: string;
  label: string;
  sheet: string;
  count: number;
  message: string;
}

/**
 * Split a rejection into the labels the broker can map right here (each listed
 * ONCE, with how many cells it blocks) and every other problem row.
 */
export function splitProblems(problems: GridProblem[]): { labels: LabelProblem[]; others: GridProblem[] } {
  const labels = new Map<string, LabelProblem>();
  const others: GridProblem[] = [];
  for (const p of problems) {
    if (p.code !== 'E_UNMAPPED_LABEL' || !p.field || !p.value) {
      others.push(p);
      continue;
    }
    // The server lists a label once per sheet; across sheets it is still one mapping.
    const key = `${p.field}|${p.value.toLowerCase()}`;
    const existing = labels.get(key);
    if (existing) {
      existing.count += p.count ?? 1;
    } else {
      labels.set(key, { field: p.field, label: p.value, sheet: p.sheet, count: p.count ?? 1, message: p.message });
    }
  }
  return { labels: [...labels.values()], others };
}

const INT_KEYS = new Set([
  'age_from',
  'age_to',
  'cc_from',
  'cc_to',
  'gvw_from_kg',
  'gvw_to_kg',
  'seating_from',
  'seating_to',
  'term_od_years',
  'term_tp_years',
]);
const LIST_KEYS = new Set(['fuel_in', 'fuel_not_in', 'business_type']);

/**
 * "lob=pcv; seating_to=3; fuel_in=cng/petrol" -> {lob: 'pcv', seating_to: 3, fuel_in: ['cng', 'petrol']}.
 * Several meanings (a label that fans out) are separated by "|". "any" means the
 * label narrows nothing ({}), e.g. an "Old" age column whose new/old difference
 * a written condition carries. Returns an error string instead when malformed.
 */
export function parseAliasText(text: string): Record<string, unknown>[] | string {
  const alternatives = text.split('|').map((alt) => alt.trim());
  const out: Record<string, unknown>[] = [];
  for (const alt of alternatives) {
    const meaning: Record<string, unknown> = {};
    if (alt.toLowerCase() === 'any') {
      out.push(meaning);
      continue;
    }
    for (const pair of alt.split(/[;,]/).map((p) => p.trim()).filter(Boolean)) {
      const [rawKey, ...rest] = pair.split('=');
      const key = rawKey?.trim();
      const value = rest.join('=').trim();
      if (!key || !value) return `"${pair}" must look like field=value.`;
      if (INT_KEYS.has(key)) {
        const n = Number(value);
        if (!Number.isInteger(n) || n < 0) return `${key} must be a whole number.`;
        meaning[key] = n;
      } else if (LIST_KEYS.has(key)) {
        meaning[key] = value.split('/').map((v) => v.trim()).filter(Boolean);
      } else {
        meaning[key] = value;
      }
    }
    out.push(meaning);
  }
  return out;
}

/** Aliases payload for the labels the broker filled in (blank entries are skipped). */
export function buildAliases(entries: { field: string; label: string; text: string }[]): GridAliases | string {
  const aliases: GridAliases = {};
  for (const e of entries) {
    if (e.field === 'geo' || !e.text.trim()) continue;
    const parsed = parseAliasText(e.text);
    if (typeof parsed === 'string') return `${e.label}: ${parsed}`;
    aliases[e.field] = aliases[e.field] ?? {};
    aliases[e.field][e.label] = parsed;
  }
  return aliases;
}

/** "BS-05, BS06 MH 12" -> ["BS-05", "BS06", "MH 12"] (the server normalises and validates). */
export function parseCodes(text: string): string[] {
  return text
    .split(/[,;\n]+/)
    .map((c) => c.trim())
    .filter(Boolean);
}

/** One line for the preview table: "25.00% of Net premium (stated on the sheet, CV!A2)". */
export function describeRate(row: Pick<GridSampleRow, 'rate' | 'premium_basis' | 'basis_source' | 'basis_ref'>): string {
  const basis = BASIS_LABELS[row.premium_basis] ?? row.premium_basis;
  const source = BASIS_SOURCE_LABELS[row.basis_source] ?? row.basis_source;
  const ref = row.basis_source === 'mapping_declared' ? '' : `, ${row.basis_ref}`;
  return `${row.rate} of ${basis} (${source}${ref})`;
}

export function problemLocation(p: Pick<GridProblem, 'sheet' | 'cell' | 'row'>): string {
  if (p.sheet && p.cell) return `${p.sheet}!${p.cell}`;
  if (p.sheet && p.row) return `${p.sheet} row ${p.row}`;
  return p.sheet || 'Whole file';
}
